import { h, Component } from 'preact';
import { Text } from 'preact-i18n';
import Popover from '../popover';
import ContainerSize from '../container-size';
import DeviceVariance from '../device-variance';
import cx from 'classnames';
import style from './style';

export default class TertiaryNav extends Component {

	render({ items, current, label='', device='', ...props }) {
		if (!items) return;

		if (!current) current = window.location.pathname;

		// Check if component explicitly wants desktop or mobile version
		if (device === 'desktop') {
			return <DesktopTertiaryNav items={items} current={current} label={label} {...props} />;
		}
		else if (device === 'mobile') {
			return <MobileTertiaryNav items={items} current={current} label={label} {...props} />;
		}

		return (
			<DeviceVariance
				adaptive
				phone={<MobileTertiaryNav items={items} current={current} label={label} {...props} />}
				other={<DesktopTertiaryNav items={items} current={current} label={label} {...props} />}
			/>
		);
	}
}

export class DesktopTertiaryNav extends Component {

	/**
	 * Apply a styled label if we're provided one, or an empty element if not.
	 * @param {String} label
	 */
	applyLabel = (label) => {
		if (label === '') {
			return ( <li><span /></li> );
		}

		return (
			<li class={cx(style.item)}>
				<span class={cx(style.category)}>{label}</span>
			</li>
		);
	};

	handleOnBeforeResize = ({ width }) => this.setState({ width });

	componentDidUpdate() {
		if (this.displayNav && this.moreNav) {
			let children = Array.prototype.slice.call(this.displayNav.childNodes).filter(
					(child) => child.getAttribute('class') === null || !child.getAttribute('class').match(style.dropdown)
				),
				more = Array.prototype.slice.call(this.displayNav.childNodes).filter(
					(child) => child.getAttribute('class') && child.getAttribute('class').match(style.dropdown)
				)[0],
				moreChildren = Array.prototype.slice.call(this.moreNav.childNodes),
				moreWidth = more ? more.offsetWidth : 0,
				maxWidth = this.props.width ? this.props.width : this.displayNav.offsetWidth,
				availableWidth = maxWidth - moreWidth,
				finalIndex = 0;

			// Handle the primary nav.
			for (let i = 0; i < children.length; i++) {
				children[i].classList.remove(cx(style.hidden));

				if (children[i].offsetHeight === 0) continue;

				// If there's not enough room to place it, hide it and set available to 0, keeping subsequent, shorter entries from being added.
				if (availableWidth - children[i].offsetWidth < 0 && i > 1) {
					children[i].classList.add(cx(style.hidden));
					availableWidth = 0;
				}
				else {
					finalIndex = i;
					availableWidth -= children[i].offsetWidth;
				}
			}

			// Hide More if we are showing the full nav. Otherwise, populate the more popover.
			if (availableWidth > 0) {
				more.classList.add(cx(style.hidden));
			}
			else {
				// Handle the 'More' list.
				more.classList.remove(cx(style.hidden));

				for (let i = 0; i < moreChildren.length; i++) {
					if (i < finalIndex) {
						moreChildren[i].classList.add(cx(style.hidden));
					}
					else {
						moreChildren[i].classList.remove(cx(style.hidden));
					}
				}
			}
		}
	}

	render({ items=[], label='', current='', dropdownText=<Text id="tertiary-nav.more" />, ...props }) {
		return (
			<div class={cx(style.root, props.class)} style={props.style}>
				<ContainerSize onBeforeResize={this.handleOnBeforeResize}>
					<ul class={cx(style.nav)} ref={c => this.displayNav=c}>

						{this.applyLabel(label)}

						{ items.map( item => (
							<li class={cx(style.item)}>
								<a href={item.url} class={cx(item.url===current && style.highlight)}>
									{item.label}
								</a>
							</li>
						)) }

						<Popover
							containerClass={style.dropdown}
							toggleClass={cx(style.more)}
							titleClass={style.moretext}
							iconPosition="right"
							text={dropdownText}
							icon="angle-down-cube"
							anchor="right"
						>
							<ul class={cx(style.dropdownmenu)} ref={c => this.moreNav=c}>
								{ items.map( item => (
									<li>
										<a href={item.url} class={cx(item.url===current && style.highlight)}>
											{item.label}
										</a>
									</li>
								)) }
							</ul>
						</Popover>
					</ul>
				</ContainerSize>
			</div>
		);
	}
}

export class MobileTertiaryNav extends Component {

	checkoutHighlight(current, item) {
		let url = item.url.substr(-1) === '/' ? item.url : item.url + '/',
			newCurrent = current.substr(-1) === '/' ? current : current + '/';

		return newCurrent === url || newCurrent.indexOf(item.highlightOn) >= 0;
	}

	render({ items, primaryLabel, secondaryLabel, current='', ...props }) {
		return (
			<header class={style.mobileHeader}>
				<nav class={style.headerNav}>
					<ul>
						{primaryLabel && <Label class={cx(primaryLabel.url===current && style.highlight, style.navItemHeader)} label={primaryLabel} />}
						{primaryLabel && secondaryLabel && <li><span class={style.labelSlash}>/</span></li>}
						{secondaryLabel && <Label class={cx(secondaryLabel.url===current && style.highlight, style.navItemHeader)} label={secondaryLabel} />}
						{
							items.map((item, index) => {
								let newWindow = item.new_window;
								//catch anything not explicitly true and default to safe value
								if (newWindow !== true) {
									newWindow = false;
								}
								const rel = newWindow ? 'noopener noreferrer' : null;
								const setTarget = newWindow ? '_blank' : '_self';
								let highlight = this.checkoutHighlight(current, item);
								return (
									<li>
										{index !== 0 && <span class={style.delimiterBar}>|</span>}
										<a class={cx(highlight && style.highlight, style.navItem)} href={item.url} target={setTarget} rel={rel}>
											{item.label}
										</a>
									</li>
								);
							})
						}
					</ul>
				</nav>
			</header>
		);
	}
}

const Label = ({ label, ...props }) => (
	<li>
		<a class={cx(style.label, props.class)} href={label.url}>
			{label.label}
		</a>
	</li>
);
